import { useState, useEffect, useMemo } from 'react';
import * as React from 'react';
import DataGrid, { SelectColumn } from 'react-data-grid';
import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query';

import {
  CalciteAction,
  CalciteActionBar,
  CalciteActionGroup,
  CalcitePanel,
  CalciteShellPanel,
} from '@esri/calcite-components-react';
import { ArcGISAuthError, IFeature } from '@esri/arcgis-rest-request';
import {
  applyEdits,
  IApplyEditsOptions,
  ILayerDefinition,
} from '@esri/arcgis-rest-feature-service';

import { makeToastError, makeToastInfo } from '../../util/toastUtils';
import { featureQuery, getLayerTotalRows } from '../../util/queryUtils';
import { buildWhereStatement } from '../../util/dataUtils';
import FilterContext from '../../contexts/FilterContext';
import isAtBottom from '../../util/layoutUtils';
import { fieldToColDef } from '../../hooks/useColumns';
import { useTableState } from '../../contexts/TableContext';
import FilterManager from '../Filters/FilterManager';
import usePanelState from '../../hooks/usePanelState';
import { CustomCalciteAction } from '../CustomCalciteAction';
import { FieldEditor } from './FieldEditor';
import { FieldManager } from './FieldManager';
import { AddFeaturePanel } from './AddFeaturePanel';
import { useAccountManager } from '../../contexts/AccountManagerContext';
import { useDebounce } from 'usehooks-ts';

interface Props {
  url: string;
  layerDef: ILayerDefinition;
  refreshTableData: () => void;
  isAdminMode: boolean;
}

function TableManager({
  url,
  layerDef,
  refreshTableData,
  isAdminMode,
}: Props): React.ReactElement {
  const { accountManager } = useAccountManager();
  const [filters, setFilters] = useState<Filters>([]);

  const [rowsPerPage] = useState(200);
  const [rows, setRows] = useState<Row[]>([]);
  const [selectedRows, setSelectedRows] = useState(new Set<number>());

  const {
    sortColumns,
    setSortColumns,
    frozenColumns,
    hiddenColumns,
    setHiddenColumns,
    onColumnVisibilityChange,
  } = useTableState();

  const [actionPanel, setActionPanel] = usePanelState<
    'Collapsed' | 'Filters' | 'Fields' | 'Add Feature' | 'Create field'
  >('Collapsed');
  // Get a list of the capabilities
  // https://developers.arcgis.com/rest/services-reference/enterprise/layer-feature-service-.htm
  const capabilities = useMemo(() => {
    if (!layerDef.capabilities) return [];
    return layerDef.capabilities.split(',') as LayerCapability[];
  }, [layerDef]);
  const whereStatement = useDebounce(buildWhereStatement(filters), 1000);

  // Query total number of rows in the layer
  const { data: layerTotalRows, error: layerTotalRowsError } = useQuery({
    queryKey: ['layerTotalRows', url, accountManager, whereStatement],
    queryFn: () => getLayerTotalRows(url, accountManager, whereStatement),
    placeholderData: 0,
  });

  // Query pages of data to display as rows
  const {
    error: rowDataError,
    data: rowData,
    refetch: fetchRowData,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'rowData',
      rowsPerPage,
      url,
      whereStatement,
      accountManager,
      sortColumns,
    ],
    queryFn: ({ pageParam = 0, signal }) =>
      featureQuery(
        pageParam,
        rowsPerPage,
        url,
        whereStatement,
        accountManager,
        sortColumns,
        signal
      ),

    getNextPageParam: (currentPage) => {
      const nextPageStart = currentPage.start + rowsPerPage;
      if (layerTotalRows && nextPageStart < layerTotalRows) {
        return nextPageStart;
      }
      return undefined;
    },
  });

  // convert fields to column definitions
  const colDefs = useMemo(
    () =>
      layerDef.fields?.map((field) =>
        fieldToColDef(
          field,
          capabilities,
          frozenColumns.includes(field.name),
          isAdminMode
        )
      ) ?? [],
    [capabilities, frozenColumns, isAdminMode, layerDef.fields]
  );

  const { mutate: applyEditsMutate } = useMutation({
    mutationFn: async (options: IApplyEditsOptions & { prevRows?: Row[] }) => {
      const { addResults, updateResults, deleteResults } = await applyEdits(
        options
      );
      if (addResults.length > 0) {
        if (!addResults[0].success) {
          throw {
            title: 'Error adding features',
            message: addResults[0].error?.description,
          };
        }

        makeToastInfo(
          `Created new feature with ObjectID: ${addResults[0].objectId}`
        );
      }

      if (updateResults.length > 0 && !updateResults[0].success) {
        throw {
          title: 'Error updating features',
          message: updateResults[0].error?.description,
        };
      }

      if (deleteResults.length > 0 && !deleteResults[0].success) {
        throw {
          title: 'Error deleting features',
          message: deleteResults[0].error?.description,
        };
      }
    },
    onError: async (error, { prevRows }) => {
      if (error instanceof ArcGISAuthError) {
        makeToastError('Authentication Error', error.message);
        return;
      }

      if (prevRows) {
        setRows(prevRows);
      }
    },
    onSettled: () => fetchRowData(),
  });

  // Side effects of when data changes
  useEffect(() => {
    if (rowData) setRows(rowData.pages.flatMap((page) => page.rows));
  }, [rowData]);

  const visibleColDefs = useMemo(
    () => colDefs.filter((colDef) => !hiddenColumns.includes(colDef.key)),
    [colDefs, hiddenColumns]
  );

  if (rowDataError) return <div>{(rowDataError as Error).message}</div>;
  if (layerTotalRowsError)
    return <div>{(layerTotalRowsError as Error).message}</div>;

  // At this point, we know status === READY
  return (
    <FilterContext.Provider value={[filters, setFilters]}>
      <div className="h-full w-full flex flex-col relative">
        {/* The main table component */}
        <DataGrid
          className="flex-1 app-theme rdg-light"
          columns={[SelectColumn, ...visibleColDefs]}
          rows={rows}
          selectedRows={selectedRows}
          rowKeyGetter={(row) =>
            row[layerDef.objectIdField ?? 'OBJECTID'] as number
          }
          onSelectedRowsChange={setSelectedRows}
          onRowsChange={(newRows, { indexes }) => {
            setRows(newRows);
            applyEditsMutate({
              url,
              authentication: accountManager,
              updates: [{ attributes: newRows[indexes[0]] }],
              prevRows: rows,
            });
          }}
          onScroll={(e) => {
            if (isAtBottom(e, 500) && hasNextPage) {
              setRows([
                ...rows,
                ...Array.from({ length: rowsPerPage }, () => ({})),
              ]);
              fetchNextPage();
            }
            e.preventDefault();
            e.stopPropagation();
          }}
          onSortColumnsChange={(cols) => setSortColumns(cols)}
          sortColumns={sortColumns}
          onCopy={(e) => {
            navigator.clipboard.writeText(JSON.stringify(e.sourceRow));
          }}
        />
      </div>
      {/* Right side action bar for Table specific actions */}
      <CalciteShellPanel
        slot="panel-end"
        position="end"
        collapsed={actionPanel === 'Collapsed'}
        widthScale="m"
        style={{
          '--calcite-shell-panel-width': '300px',
        }}
      >
        <CalciteActionBar slot="action-bar">
          <CalciteActionGroup>
            <CustomCalciteAction
              selectedPanel={actionPanel}
              panelName="Add Feature"
              onClick={setActionPanel}
              tooltip="Add Feature"
              icon="plus"
              disabled={!capabilities.includes('Create')}
            />

            <CustomCalciteAction
              selectedPanel={actionPanel}
              panelName="Delete selected features"
              onClick={() => {
                if (selectedRows.size > 0)
                  applyEditsMutate({
                    url,
                    authentication: accountManager,
                    deletes: [...selectedRows],
                  });
              }}
              tooltip="Delete selected features"
              icon="trash"
              disabled={
                !capabilities.includes('Delete') || selectedRows.size === 0
              }
            />
          </CalciteActionGroup>
          <CalciteActionGroup>
            <CustomCalciteAction
              selectedPanel={actionPanel}
              panelName="Filters"
              onClick={() => setActionPanel('Filters')}
              tooltip="Applied Filters"
              icon="filter"
              badgeContent={
                filters.length > 0 ? filters.length.toString() : undefined
              }
            />
            <CustomCalciteAction
              selectedPanel={actionPanel}
              panelName="Fields"
              onClick={setActionPanel}
              tooltip="Table Fields"
              icon="description"
              disabled={!capabilities.includes('Query')}
            />
            <CustomCalciteAction
              selectedPanel={actionPanel}
              panelName="Create field"
              onClick={setActionPanel}
              tooltip="Create new fields"
              icon="group-form-plus"
              disabled={!isAdminMode}
            />
          </CalciteActionGroup>
        </CalciteActionBar>

        <CalcitePanel
          heading="Add Feature"
          hidden={actionPanel !== 'Add Feature'}
        >
          {accountManager && actionPanel === 'Add Feature' && (
            <AddFeaturePanel
              layerDef={layerDef}
              session={accountManager}
              onAddFeature={(feature: IFeature) => {
                applyEditsMutate({
                  url,
                  authentication: accountManager,
                  adds: [feature],
                });
              }}
            />
          )}
        </CalcitePanel>

        <CalcitePanel heading="Filters" hidden={actionPanel !== 'Filters'}>
          {colDefs && <FilterManager colDefs={colDefs} />}
        </CalcitePanel>
        <CalcitePanel heading="Fields" hidden={actionPanel !== 'Fields'}>
          <CalciteAction
            slot="header-actions-end"
            icon={
              hiddenColumns.length === 0
                ? 'view-visible'
                : hiddenColumns.length === colDefs.length
                ? 'view-hide'
                : 'view-mixed'
            }
            text="Show all"
            onClick={() => {
              if (hiddenColumns.length === 0) {
                setHiddenColumns(colDefs.map((c) => c.key));
              } else {
                setHiddenColumns([]);
              }
            }}
          />
          {layerDef.fields && (
            <FieldManager
              layerDef={layerDef}
              layerUrl={url}
              isAdminMode={isAdminMode}
              hiddenFields={hiddenColumns}
              onVisibilityChange={onColumnVisibilityChange}
            />
          )}
        </CalcitePanel>
        <CalcitePanel
          heading="Create a field"
          hidden={actionPanel !== 'Create field'}
        >
          <FieldEditor
            mode="create"
            layerDefinition={layerDef}
            layerUrl={url}
            onFieldCreated={() => refreshTableData()}
          />
        </CalcitePanel>
      </CalciteShellPanel>
    </FilterContext.Provider>
  );
}
export default TableManager;
